import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import PackagePickerModal from '../../components/PackagePickerModal'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

// Generates ['Feb-24', 'Mar-24', ...] inclusive between two 'YYYY-MM'
// strings. Shared by every section that needs a month-phasing table
// (normal Indirect items, Risk & Contingency, HO Overhead) so there is
// exactly one place that defines what a "project month" is.
// Distribution pattern generators. Given a total budget and an ordered
// list of month keys, each returns a { month: amount } map. These are
// applied ONCE to pre-fill the editor; the Estimator can still hand-edit
// any individual month afterward (pattern is a starting point, not a
// locked formula). Every pattern's raw output is corrected in the last
// month so the sum matches `total` exactly (avoids floating leftovers).
function applyDistributionPattern(pattern, total, monthKeys) {
  const n = monthKeys.length
  if (n === 0 || !total) return {}
  let weights
  if (pattern === 'even') {
    weights = monthKeys.map(() => 1 / n)
  } else if (pattern === 'front_loaded') {
    // Linearly decreasing weight: first month heaviest.
    weights = monthKeys.map((_, i) => (n - i))
    const s = weights.reduce((a, b) => a + b, 0)
    weights = weights.map((w) => w / s)
  } else if (pattern === 'back_loaded') {
    weights = monthKeys.map((_, i) => (i + 1))
    const s = weights.reduce((a, b) => a + b, 0)
    weights = weights.map((w) => w / s)
  } else if (pattern === 's_curve') {
    // Classic cumulative logistic S-Curve: slow start, fast middle,
    // slow finish. Weight of month i = CDF(i+1) - CDF(i).
    const k = 10 / Math.max(n, 1) // steepness scaled to duration
    const mid = n / 2
    const cdf = (x) => 1 / (1 + Math.exp(-k * (x - mid)))
    const raw = []
    for (let i = 0; i <= n; i++) raw.push(cdf(i))
    const span = raw[n] - raw[0]
    weights = []
    for (let i = 0; i < n; i++) weights.push((raw[i + 1] - raw[i]) / span)
  } else {
    return {}
  }
  const result = {}
  let running = 0
  monthKeys.forEach((m, i) => {
    if (i === n - 1) {
      result[m] = Math.round((total - running) * 100) / 100 // last month absorbs rounding
    } else {
      const v = Math.round(total * weights[i] * 100) / 100
      result[m] = v
      running += v
    }
  })
  return result
}

function generateMonthKeys(fromYm, toYm) {
  if (!fromYm || !toYm) return []
  const [fy, fm] = fromYm.split('-').map(Number)
  const [ty, tm] = toYm.split('-').map(Number)
  const keys = []
  let y = fy, m = fm
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  while (y < ty || (y === ty && m <= tm)) {
    keys.push(`${monthNames[m - 1]}-${String(y).slice(2)}`)
    m++
    if (m > 12) { m = 1; y++ }
  }
  return keys
}

// Sorts 'Mon-YY' keys ('Feb-24', 'Mar-24', ...) into true chronological
// order. Object key order in JS is not guaranteed to match insertion
// order once a distribution has been edited/re-saved multiple times,
// so display order must never rely on Object.keys(dist) directly --
// this is the single source of truth for "what comes before what".
function sortMonthKeys(keys) {
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return [...keys].sort((a, b) => {
    const [ma, ya] = a.split('-')
    const [mb, yb] = b.split('-')
    const va = Number(ya) * 12 + monthNames.indexOf(ma)
    const vb = Number(yb) * 12 + monthNames.indexOf(mb)
    return va - vb
  })
}

// Read-only horizontal strip for viewing a saved monthly distribution.
// Stays a single row and scrolls sideways for any number of months
// (8 or 48) instead of wrapping to new lines or growing tall.
function DistributionTable({ dist }) {
  const months = sortMonthKeys(Object.keys(dist))
  const total = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
  return (
    <div style={{ width: '100%', paddingLeft: 16 }}>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
        {months.map((m) => (
          <div key={m} style={{ flex: '0 0 auto', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', textAlign: 'center', minWidth: 90 }}>
            <div style={{ fontSize: 10, color: colors.textMuted, marginBottom: 2, whiteSpace: 'nowrap' }}>{m}</div>
            <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{formatNumber(dist[m])}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700 }}>Total: {formatNumber(total)}</div>
    </div>
  )
}

// Searchable combobox with "create new" support. Options come from
// whatever the caller already has in memory (rows already loaded on
// this page) -- no new query, no master table. Prevents visually
// duplicate options caused by whitespace/case differences on NEW
// entries only; never touches existing stored values.
function ComboBox({ value, options, onChange, placeholder }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useState(() => Math.random().toString(36))[0]

  // When the caller's value changes from outside (e.g. startEdit sets
  // a different record's department), reset the search text so the
  // combobox doesn't show stale typed characters -- the current value
  // itself is now shown via the placeholder, not lost.
  useEffect(() => { setQuery('') }, [value])

  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(`[data-combobox="${ref}"]`)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [ref])

  const normalized = (s) => (s || '').trim().toLowerCase()
  const filtered = options.filter((o) => normalized(o).includes(normalized(query)))
  const exactExists = options.some((o) => normalized(o) === normalized(query))
  const trimmedQuery = (query || '').trim()
  // Practical fix for growing lists: with no search text typed yet,
  // show only the first few options plus a hint to keep typing --
  // never dump the whole (potentially long) list on open.
  const MAX_UNFILTERED = 6
  const displayed = trimmedQuery ? filtered : filtered.slice(0, MAX_UNFILTERED)
  const hiddenCount = trimmedQuery ? 0 : Math.max(0, filtered.length - MAX_UNFILTERED)

  return (
    <div data-combobox={ref} style={{ position: 'relative', width: 180 }}>
      <input
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={value || placeholder}
        style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: `1px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 2,
          background: '#FFF', border: `1px solid ${colors.border}`, borderRadius: 6,
          maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {value && (
            <div onClick={() => { onChange(null); setOpen(false) }}
              style={{ padding: '8px 12px', fontSize: 12, color: colors.textMuted, cursor: 'pointer', borderBottom: `1px solid ${colors.border}` }}>
              ✕ Clear
            </div>
          )}
          {displayed.map((o) => (
            <div key={o} onClick={() => { onChange(o); setOpen(false) }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F1F5F9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              {o}
            </div>
          ))}
          {hiddenCount > 0 && (
            <div style={{ padding: '6px 12px', fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
              +{hiddenCount} more — type to search…
            </div>
          )}
          {trimmedQuery && !exactExists && (
            <div onClick={() => { onChange(trimmedQuery); setOpen(false) }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: colors.textSecondary, fontStyle: 'italic', borderTop: filtered.length ? `1px solid ${colors.border}` : 'none' }}>
              + Create "{trimmedQuery}"
            </div>
          )}
          {filtered.length === 0 && !trimmedQuery && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: colors.textMuted }}>Type to search or create…</div>
          )}
        </div>
      )}
    </div>
  )
}

// Cross-section shared option state. All three sections read/write
// the SAME package/department lists (per the approved architecture:
// a value created in any one section is immediately selectable in the
// other two), sourced from the three already-loaded tables -- no new
// query beyond what each section already fetches, no master table.
function useSharedOptions(revisionId, tenderId) {
  const [departments, setDepartments] = useState([])
  const [packageNameById, setPackageNameById] = useState({})
  // Tender Currency -- single authoritative source for every monetary
  // display across all three sections (Indirect, Risk, HO) below. Fetched
  // once here (already the single shared hook all three sections consume
  // via `shared`) rather than three separate per-section queries.
  const [tenderCurrency, setTenderCurrency] = useState(null)

  const refresh = useCallback(async () => {
    const [ind, risk, ho, pkgs, tenderRes] = await Promise.all([
      supabase.from('indirect_cost_item').select('department').eq('estimate_revision_id', revisionId),
      supabase.from('risk_contingency_line').select('department').eq('estimate_revision_id', revisionId),
      supabase.from('ho_overhead_setting').select('department').eq('estimate_revision_id', revisionId),
      supabase.schema('estimation').from('boq_package').select('id, name').eq('tender_id', tenderId),
      supabase.from('tender').select('currency').eq('id', tenderId).maybeSingle(),
    ])
    const all = [...(ind.data || []), ...(risk.data || []), ...(ho.data || [])]
    setDepartments([...new Set(all.map(r => r.department).filter(Boolean))].sort())
    setPackageNameById(Object.fromEntries((pkgs.data || []).map(p => [p.id, p.name])))
    setTenderCurrency(tenderRes.data?.currency ?? null)
  }, [revisionId, tenderId])

  useEffect(() => { refresh() }, [refresh])

  return { departments, packageNameById, tenderCurrency, refreshSharedOptions: refresh }
}

// Section totals -- read verbatim from the existing authoritative
// calculation_chain view. No React-side sum, no recalculation: this
// is a display-only hook that mirrors the same SSOT used everywhere
// else in the app (Tender Summary, Indirect Budget Review, exports).
function useSectionTotals(revisionId) {
  const [totals, setTotals] = useState(null)
  const [isFrozen, setIsFrozen] = useState(false)

  const refresh = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    const frozen = revRes.data?.status === 'frozen'
    setIsFrozen(frozen)
    if (frozen) {
      // Frozen: estimate_revision_calculation_chain returns zeros for a
      // frozen revision (its live source tables are empty by then).
      // Read the same three totals from the immutable freeze_snapshot
      // instead -- general_site_overhead_total/general_contingency_total
      // live inside full_indirect_data, ho_overhead_total is a direct
      // column -- both already proven exact against the live View in
      // prior validation (TenderSummaryPage/P1). No new calculation.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('ho_overhead_total, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      const indirect = snapRes.data?.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      setTotals({
        general_site_overhead_total: indirect.general_site_overhead_total ?? 0,
        general_contingency_total: indirect.general_contingency_total ?? 0,
        ho_overhead_total: snapRes.data?.ho_overhead_total ?? 0,
      })
      return
    }
    const { data } = await supabase
      .from('estimate_revision_calculation_chain')
      .select('general_site_overhead_total, general_contingency_total, ho_overhead_total')
      .eq('estimate_revision_id', revisionId)
      .maybeSingle()
    setTotals(data || { general_site_overhead_total: 0, general_contingency_total: 0, ho_overhead_total: 0 })
  }, [revisionId])

  useEffect(() => { refresh() }, [refresh])

  return { totals, isFrozen, refreshTotals: refresh }
}

export default function IndirectRiskHoPage() {
  const { tenderId, revisionId } = useParams()
  const shared = useSharedOptions(revisionId, tenderId)
  const { totals, isFrozen, refreshTotals } = useSectionTotals(revisionId)

  return (
    <div>
      <EstimationBreadcrumb />
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 20px' }}>
        Indirect Costs, Risk &amp; Overheads
      </h1>
      {isFrozen && (
        <div style={{ padding: '10px 16px', background: '#F1F5F9', color: colors.textSecondary, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          🔒 This revision is frozen — showing the immutable historical snapshot. Editing is disabled.
        </div>
      )}

      <IndirectCostSection revisionId={revisionId} tenderId={tenderId} shared={shared} sectionTotal={totals?.general_site_overhead_total} isFrozen={isFrozen} refreshTotals={refreshTotals} />
      <div style={{ height: 28 }} />
      <RiskSection revisionId={revisionId} tenderId={tenderId} shared={shared} sectionTotal={totals?.general_contingency_total} isFrozen={isFrozen} refreshTotals={refreshTotals} />
      <div style={{ height: 28 }} />
      <HoOverheadSection revisionId={revisionId} tenderId={tenderId} shared={shared} sectionTotal={totals?.ho_overhead_total} isFrozen={isFrozen} refreshTotals={refreshTotals} />
    </div>
  )
}

function IndirectCostSection({ revisionId, tenderId, shared, sectionTotal, isFrozen, refreshTotals }) {
  const [items, setItems] = useState(null)
  const [packages, setPackages] = useState([])
  const [boqItems, setBoqItems] = useState([])
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const [description, setDescription] = useState('')
  const [costNature, setCostNature] = useState('time_related')
  const [monthlyRate, setMonthlyRate] = useState('')
  const [fixedAmount, setFixedAmount] = useState('')
  const [allocationScope, setAllocationScope] = useState('revision_general')
  const [allocationRef, setAllocationRef] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null) // null = Add mode; else editing this item's id
  const [packageId, setPackageId] = useState(null)
  const [showPackagePicker, setShowPackagePicker] = useState(false)
  const [department, setDepartment] = useState('')
  const [pattern, setPattern] = useState('manual')
  // New Estimation-owned pricing fields (Phase 2A). indirect_budget_rate
  // is the explicit pricing field going forward; monthly_rate is kept
  // in sync internally only to satisfy the existing chk_cost_nature_
  // fields constraint -- the user never enters it twice.
  const [qty, setQty] = useState('')
  const [unit, setUnit] = useState('month')
  const [indirectBudgetRate, setIndirectBudgetRate] = useState('')
  const [monthlyDistribution, setMonthlyDistribution] = useState({}) // { 'Feb-24': 1000, ... }
  const [fromMonth, setFromMonth] = useState('') // 'YYYY-MM'
  const [toMonth, setToMonth] = useState('')     // 'YYYY-MM'
  const [expandedItems, setExpandedItems] = useState(new Set())

  const load = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    if (revRes.data?.status === 'frozen') {
      // Frozen: read the immutable full_indirect_data.site_overhead_items
      // instead of the live indirect_cost_item table (empty by then, per
      // proven evidence). packages/boqItems feed the Add/Edit form's
      // dropdowns only -- left empty since editing is disabled here anyway.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      const indirect = snapRes.data?.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      setItems(Array.isArray(indirect.site_overhead_items) ? indirect.site_overhead_items : [])
      setPackages([])
      setBoqItems([])
      return
    }
    const [itemsRes, pkgRes, boqRes] = await Promise.all([
      supabase.from('indirect_cost_item').select('*').eq('estimate_revision_id', revisionId),
      supabase.from('wbs_snapshot_node').select('id, name').eq('estimate_revision_id', revisionId).eq('is_costing_package', true),
      supabase.from('boq_item').select('id, item_no, description').eq('estimate_revision_id', revisionId).order('item_no'),
    ])
    if (itemsRes.error) return setError(itemsRes.error.message)
    setItems(itemsRes.data)
    setPackages(pkgRes.data || [])
    setBoqItems(boqRes.data || [])
  }, [revisionId])

  useEffect(() => { load() }, [load])

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)

    // Effective Qty for time_related items is always derived from the
    // selected From/To period -- never the free-text `qty` state (that
    // field is disabled/derived in the UI for this cost_nature).
    const monthKeys = generateMonthKeys(fromMonth, toMonth)
    const effectiveQty = costNature === 'time_related' ? monthKeys.length : (qty ? Number(qty) : null)

    const distSum = Object.values(monthlyDistribution).reduce((s, v) => s + (Number(v) || 0), 0)
    const expectedBudget = effectiveQty && indirectBudgetRate ? Number(effectiveQty) * Number(indirectBudgetRate) : (costNature === 'fixed' ? Number(fixedAmount) : null)
    if (Object.keys(monthlyDistribution).length > 0 && expectedBudget != null && Math.abs(distSum - expectedBudget) > 0.01) {
      setError(`Monthly distribution (${distSum}) does not reconcile with Tender Budget (${expectedBudget}). Adjust the monthly values before saving.`)
      setSubmitting(false)
      return
    }

    const payload = {
      estimate_revision_id: revisionId,
      description,
      cost_nature: costNature,
      // monthly_rate is kept internally equal to indirect_budget_rate
      // for time_related items solely to satisfy the existing DB
      // constraint (chk_cost_nature_fields) -- the user only ever sees
      // and enters "Indirect Budget Rate" once.
      monthly_rate: costNature === 'time_related' ? Number(indirectBudgetRate || monthlyRate) : null,
      fixed_amount: costNature === 'fixed' ? Number(fixedAmount) : null,
      // The UI's own scope key stays 'boq_specific' (matches the <option>
      // value and every allocationScope === 'boq_specific' check below).
      // Only the value actually persisted to the DB is translated here --
      // every financial view (boq_item_specific_risk,
      // boq_item_specific_site_overhead, and their *_via_package
      // counterparts) filters on allocation_scope = 'specific'; a row
      // saved as 'boq_specific' was silently excluded from those totals.
      allocation_scope: allocationScope === 'boq_specific' ? 'specific' : allocationScope,
      allocation_wbs_node_id: allocationScope === 'package_specific' ? allocationRef : null,
      allocation_boq_item_id: allocationScope === 'boq_specific' ? allocationRef : null,
      qty: effectiveQty,
      unit: unit || null,
      indirect_budget_rate: indirectBudgetRate ? Number(indirectBudgetRate) : null,
      planned_monthly_distribution: monthlyDistribution,
      from_month: fromMonth || null,
      to_month: toMonth || null,
      // Cost Package and Department/Category are independent classification
      // fields (Cost Control), unrelated to allocation_scope/WBS. Neither
      // is derived from the other or from WBS.
      package_id: packageId,
      department: department || null,
    }

    const { error } = editingId
      ? await supabase.from('indirect_cost_item').update(payload).eq('id', editingId)
      : await supabase.from('indirect_cost_item').insert(payload)
    if (error) setError(error.message)
    else {
      setSuccessMsg(editingId ? 'Indirect cost item updated.' : 'Indirect cost item added.')
      setDescription(''); setMonthlyRate(''); setFixedAmount(''); setAllocationRef('')
      setQty(''); setIndirectBudgetRate(''); setMonthlyDistribution({}); setFromMonth(''); setToMonth('')
      setPackageId(null); setDepartment(''); setPattern('manual')
      setEditingId(null)
      await load()
      await shared.refreshSharedOptions(); await refreshTotals()
    }
    setSubmitting(false)
  }

  function startEdit(item) {
    setEditingId(item.id)
    setDescription(item.description)
    setCostNature(item.cost_nature)
    setMonthlyRate(item.monthly_rate ?? '')
    setFixedAmount(item.fixed_amount ?? '')
    // DB rows store 'specific' (see the save-path comment above); the
    // <select> option/UI key is 'boq_specific' -- translate back on read
    // so editing an existing BOQ-specific row shows the correct selection
    // instead of an unmatched (blank-looking) <select> value.
    setAllocationScope(item.allocation_scope === 'specific' ? 'boq_specific' : item.allocation_scope)
    setAllocationRef(item.allocation_wbs_node_id || item.allocation_boq_item_id || '')
    setQty(item.qty ?? '')
    setUnit(item.unit || 'month')
    setIndirectBudgetRate(item.indirect_budget_rate ?? '')
    setMonthlyDistribution(item.planned_monthly_distribution || {})
    // Now stored explicitly (from_month/to_month columns), so editing
    // an item restores its actual period instead of forcing the
    // Estimator to re-pick it just to change an unrelated field.
    setFromMonth(item.from_month || '')
    setToMonth(item.to_month || '')
    setPackageId(item.package_id || null)
    setDepartment(item.department || '')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function cancelEdit() {
    setEditingId(null)
    setDescription(''); setMonthlyRate(''); setFixedAmount(''); setAllocationRef('')
    setQty(''); setIndirectBudgetRate(''); setMonthlyDistribution({}); setFromMonth(''); setToMonth('')
    setPackageId(null); setDepartment(''); setPattern('manual')
  }

  async function handleDelete(id) {
    setError(null)
    const { error } = await supabase.from('indirect_cost_item').delete().eq('id', id)
    if (error) setError(error.message)
    else { setSuccessMsg('Removed.'); await load() }
  }

  function scopeLabel(item) {
    if (item.allocation_scope === 'revision_general') return 'Revision General'
    if (item.allocation_scope === 'package_specific') return `Package: ${packages.find(p => p.id === item.allocation_wbs_node_id)?.name || '—'}`
    return `BOQ: ${boqItems.find(b => b.id === item.allocation_boq_item_id)?.item_no || '—'}`
  }

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 4px' }}>Indirect Costs (Preliminaries)</h2>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 12px' }}>
        Site overhead items — time-related (monthly rate × duration) or fixed lump sum. No markup ever applies to Revision-General items.
      </p>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {!isFrozen && (
      <>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} required style={{ width: 220 }} />
        </Field>
        <Field label="Cost Nature">
          <Select value={costNature} onChange={(e) => setCostNature(e.target.value)} style={{ width: 140 }}>
            <option value="time_related">Time-related</option>
            <option value="fixed">Fixed</option>
          </Select>
        </Field>
        {costNature === 'time_related' ? (
          <Field label={`Indirect Budget Rate (${shared.tenderCurrency || '—'}/month)`}>
            <Input type="number" step="any" min="0" value={indirectBudgetRate} onChange={(e) => setIndirectBudgetRate(e.target.value)} required style={{ width: 140 }} />
          </Field>
        ) : (
          <Field label="Fixed Amount">
            <Input type="number" step="any" min="0" value={fixedAmount} onChange={(e) => setFixedAmount(e.target.value)} required style={{ width: 120 }} />
          </Field>
        )}
        {costNature === 'time_related' && (
          <>
            <Field label="From Month">
              <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} required style={{ width: 150 }} />
            </Field>
            <Field label="To Month">
              <Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} required style={{ width: 150 }} />
            </Field>
          </>
        )}
        <Field label="Qty">
          {/* Time-related Qty is derived from the selected period, not
              independently entered -- a period of Feb..May is always
              4 months, never a mismatched manual number. Fixed items
              keep a free Qty field since they have no time dimension. */}
          {costNature === 'time_related' ? (
            <Input type="number" value={generateMonthKeys(fromMonth, toMonth).length || ''} readOnly disabled style={{ width: 90, background: '#F1F5F9' }} />
          ) : (
            <Input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 90 }} />
          )}
        </Field>
        <Field label="Unit">
          <Input value={unit} onChange={(e) => setUnit(e.target.value)} style={{ width: 90 }} />
        </Field>
        <Field label="Allocation">
          <Select value={allocationScope} onChange={(e) => { setAllocationScope(e.target.value); setAllocationRef('') }} style={{ width: 160 }}>
            <option value="revision_general">Revision General</option>
            <option value="package_specific">Package-specific</option>
            <option value="boq_specific">BOQ-specific</option>
          </Select>
        </Field>
        {allocationScope === 'package_specific' && (
          <Field label="Package">
            <Select value={allocationRef} onChange={(e) => setAllocationRef(e.target.value)} required style={{ width: 200 }}>
              <option value="" disabled>Select…</option>
              {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}
        {allocationScope === 'boq_specific' && (
          <Field label="BOQ Item">
            <Select value={allocationRef} onChange={(e) => setAllocationRef(e.target.value)} required style={{ width: 240 }}>
              <option value="" disabled>Select…</option>
              {boqItems.map(b => <option key={b.id} value={b.id}>{b.item_no} — {b.description}</option>)}
            </Select>
          </Field>
        )}
        {/* Independent classification fields (Cost Control), not to be
            confused with the WBS "Package" allocation above. Neither is
            derived from the other. */}
        {/* Independent classification fields (Cost Control), not to be
            confused with the WBS "Package" allocation above. Neither is
            derived from the other. Options are the DISTINCT non-null
            values already present in this revision's loaded items --
            no new query, no master table. */}
        <Field label="Cost Package">
          <button type="button" onClick={() => setShowPackagePicker(true)}
            style={{ fontSize: 12, padding: '6px 10px', border: '1px solid #DDD9D0', borderRadius: 5, background: '#fff', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
            {shared.packageNameById?.[packageId] || '— Select —'}
          </button>
        </Field>
        <Field label="Department / Category">
          <ComboBox value={department} onChange={(v) => setDepartment(v || '')}
            options={shared.departments}
            placeholder="e.g. Construction Dep." />
        </Field>
        <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : (editingId ? 'Update' : '+ Add')}</Button>
        {editingId && <Button type="button" variant="secondary" onClick={cancelEdit}>Cancel</Button>}
      </form>

      {(() => {
        const monthKeys = generateMonthKeys(fromMonth, toMonth)
        const qtyNum = costNature === 'time_related' ? monthKeys.length : (Number(qty) || 0)
        const rateNum = Number(indirectBudgetRate) || 0
        const budget = costNature === 'time_related' ? qtyNum * rateNum : (Number(fixedAmount) || 0)
        const budgetReady = budget > 0
        const distSum = Object.values(monthlyDistribution).reduce((s, v) => s + (Number(v) || 0), 0)
        const variance = budget - distSum
        const reconciled = monthKeys.length === 0 || Math.abs(variance) <= 0.01

        return (
          <div style={{ padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Tender Budget</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: budgetReady ? colors.textPrimary : colors.textMuted }}>
                {formatNumber(budget)}
              </span>
              {costNature === 'time_related' && <span style={{ fontSize: 11, color: colors.textMuted }}>({formatNumber(qtyNum)} months × {formatNumber(rateNum)})</span>}
            </div>

            {!budgetReady ? (
              <div style={{ padding: '10px 14px', background: '#F1F5F9', borderRadius: 6, fontSize: 12, color: colors.textMuted }}>
                Enter {costNature === 'time_related' ? 'a From/To period and Indirect Budget Rate' : 'a Fixed Amount'} above to enable monthly phasing.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Planned Monthly Distribution ({fromMonth || '—'} to {toMonth || '—'})</span>
                  <Field label="Pattern">
                    <Select value={pattern} onChange={(e) => {
                      const p = e.target.value
                      setPattern(p)
                      if (p === 'manual') return
                      setMonthlyDistribution(applyDistributionPattern(p, budget, monthKeys))
                    }} style={{ width: 140 }}>
                      <option value="manual">Manual</option>
                      <option value="even">Even</option>
                      <option value="s_curve">S-Curve</option>
                      <option value="front_loaded">Front-Loaded</option>
                      <option value="back_loaded">Back-Loaded</option>
                    </Select>
                  </Field>
                </div>

                {monthKeys.length > 0 && (
                  <>
                    {/* Single horizontal row, scrolls sideways -- stays
                        this shape whether it's 8 months or 48 (4
                        years); never wraps or grows tall. */}
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginBottom: 12, paddingBottom: 8 }}>
                      {monthKeys.map((m) => (
                        <div key={m} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 }}>
                          <label style={{ fontSize: 10, color: colors.textMuted, whiteSpace: 'nowrap' }}>{m}</label>
                          <Input
                            type="number" step="any" value={monthlyDistribution[m] || ''}
                            onChange={(e) => setMonthlyDistribution((prev) => ({ ...prev, [m]: Number(e.target.value) || 0 }))}
                            style={{ width: 100 }}
                          />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                      <span>Tender Budget: <strong>{formatNumber(budget)}</strong></span>
                      <span>Planned Distribution: <strong>{formatNumber(distSum)}</strong></span>
                      <span>Variance: <strong style={{ color: reconciled ? '#065F46' : '#991B1B' }}>{formatNumber(variance)}</strong></span>
                      <span style={{ fontWeight: 700, color: reconciled ? '#065F46' : '#991B1B' }}>
                        {reconciled ? '✅ Reconciled' : '🚫 ERROR — Not Reconciled'}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )
      })()}
      </>
      )}

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'auto' }}>
        {items === null && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={3} /></div>}
        {items?.length === 0 && <div style={{ padding: 20 }}><EmptyState title="No indirect cost items yet" /></div>}
        {items?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
              <Th></Th><Th>Description</Th><Th>Cost Package</Th><Th>Department / Category</Th><Th>Nature</Th><Th align="right">Qty</Th><Th>Unit</Th><Th align="right">Rate</Th><Th>Allocation</Th><Th align="right">Amount</Th><Th>Monthly Distribution</Th><Th></Th>
            </tr></thead>
            <tbody>
              {items.map(i => {
                const dist = i.planned_monthly_distribution || {}
                const months = sortMonthKeys(Object.keys(dist))
                const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
                // Full Tender Budget = qty (months) x monthly rate for
                // time_related items -- comparing distSum against the
                // monthly rate alone (a single month's amount) was the
                // bug that made every correctly-phased distribution
                // show as a mismatch.
                const budget = i.cost_nature === 'time_related' ? ((i.qty || 0) * (i.monthly_rate || 0)) : (i.fixed_amount || 0)
                const hasDistribution = months.length > 0
                const reconciled = hasDistribution && Math.abs(budget - distSum) <= 0.01
                const isOpen = expandedItems.has(i.id)
                return (
                  <>
                    <tr key={i.id} style={{ borderBottom: `1px solid ${colors.border}`, cursor: hasDistribution ? 'pointer' : 'default' }}
                        onClick={() => hasDistribution && setExpandedItems((prev) => {
                          const next = new Set(prev)
                          next.has(i.id) ? next.delete(i.id) : next.add(i.id)
                          return next
                        })}>
                      <Td>{hasDistribution ? (isOpen ? '▾' : '▸') : ''}</Td>
                      <Td>{i.description}</Td>
                      <Td muted>{i.package_name || '—'}</Td>
                      <Td muted>{i.department || '—'}</Td>
                      <Td muted>{i.cost_nature === 'time_related' ? 'Time-related' : 'Fixed'}</Td>
                      <Td align="right">{i.qty != null ? formatNumber(i.qty) : '—'}</Td>
                      <Td muted>{i.unit || '—'}</Td>
                      <Td align="right">{i.indirect_budget_rate != null ? formatNumber(i.indirect_budget_rate) : '—'}</Td>
                      <Td muted>{scopeLabel(i)}</Td>
                      <Td align="right">{i.cost_nature === 'time_related' ? `${formatNumber(i.monthly_rate)}/mo` : formatNumber(i.fixed_amount)}</Td>
                      {/* This is the check that matters for CostSpace export:
                          an item with no monthly distribution at all will
                          still export its Tender Budget, but has no phasing
                          data to accompany it -- flagged clearly here. */}
                      <Td>
                        {!hasDistribution ? (
                          <span style={{ color: '#92400E', fontWeight: 600, fontSize: 12 }}>⚠ No distribution</span>
                        ) : reconciled ? (
                          <span style={{ color: '#065F46', fontWeight: 600, fontSize: 12 }}>✅ {months.length} months</span>
                        ) : (
                          <span style={{ color: '#991B1B', fontWeight: 600, fontSize: 12 }}>🚫 Mismatch</span>
                        )}
                      </Td>
                      <Td align="right">
                        <button onClick={(e) => { e.stopPropagation(); startEdit(i) }} style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: 13, marginRight: 10 }}>Edit</button>
                        {!isFrozen && <button onClick={(e) => { e.stopPropagation(); handleDelete(i.id) }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 13 }}>Remove</button>}
                      </Td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={12} style={{ padding: '0 0 14px 0' }}>
                          <DistributionTable dist={dist} />
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Display-only total, read verbatim from
          estimate_revision_general_site_overhead via
          estimate_revision_calculation_chain -- never summed in
          React. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 4px', borderTop: `2px solid ${colors.border}`, marginTop: -1 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
          Normal Indirect Total: {formatNumber(sectionTotal ?? 0)} {shared.tenderCurrency || ''}
        </span>
      </div>
      {showPackagePicker && (
        <PackagePickerModal
          tenderId={tenderId}
          currentPackageId={packageId}
          onSelect={async (id) => { setPackageId(id); setShowPackagePicker(false); await shared.refreshSharedOptions() }}
          onClose={() => setShowPackagePicker(false)}
        />
      )}
    </section>
  )
}

function RiskSection({ revisionId, tenderId, shared, sectionTotal, isFrozen, refreshTotals }) {
  const [lines, setLines] = useState(null)
  const [packages, setPackages] = useState([])
  const [boqItems, setBoqItems] = useState([])
  const [calcTotal, setCalcTotal] = useState(null) // authoritative SAR value from calculation_chain
  const [directCostTotal, setDirectCostTotal] = useState(0)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const [description, setDescription] = useState('')
  const [allocationScope, setAllocationScope] = useState('revision_general')
  const [allocationRef, setAllocationRef] = useState('')
  const [inputMode, setInputMode] = useState('pct')
  const [amount, setAmount] = useState('')
  const [pct, setPct] = useState('')
  const [fromMonth, setFromMonth] = useState('')
  const [toMonth, setToMonth] = useState('')
  const [monthlyDistribution, setMonthlyDistribution] = useState({})
  const [riskPattern, setRiskPattern] = useState('manual')
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [packageId, setPackageId] = useState(null)
  const [showPackagePicker, setShowPackagePicker] = useState(false)
  const [department, setDepartment] = useState('')
  const [expandedLines, setExpandedLines] = useState(new Set())
  const [distEditor, setDistEditor] = useState(null) // { lineId, fromMonth, toMonth, values }

  const load = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    if (revRes.data?.status === 'frozen') {
      // Frozen: read risk_lines and the authoritative totals from the
      // immutable freeze_snapshot instead of the live (empty) tables/View.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('direct_cost_total, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      const indirect = snapRes.data?.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      setLines(Array.isArray(indirect.risk_lines) ? indirect.risk_lines : [])
      setPackages([])
      setBoqItems([])
      setCalcTotal(indirect.general_contingency_total ?? 0)
      setDirectCostTotal(snapRes.data?.direct_cost_total ?? 0)
      return
    }
    const [linesRes, pkgRes, boqRes, chainRes] = await Promise.all([
      supabase.from('risk_contingency_line').select('*').eq('estimate_revision_id', revisionId),
      supabase.from('wbs_snapshot_node').select('id, name').eq('estimate_revision_id', revisionId).eq('is_costing_package', true),
      supabase.from('boq_item').select('id, item_no, description').eq('estimate_revision_id', revisionId).order('item_no'),
      // Authoritative monetary value -- same SSOT that produces the
      // Golden Project total. Never recalculated here.
      supabase.from('estimate_revision_calculation_chain').select('general_contingency_total, direct_cost_total').eq('estimate_revision_id', revisionId).maybeSingle(),
    ])
    if (linesRes.error) return setError(linesRes.error.message)
    setLines(linesRes.data)
    setPackages(pkgRes.data || [])
    setBoqItems(boqRes.data || [])
    setCalcTotal(chainRes.data?.general_contingency_total ?? 0)
    setDirectCostTotal(chainRes.data?.direct_cost_total ?? 0)
  }, [revisionId])

  useEffect(() => { load() }, [load])

  const effectiveMode = allocationScope === 'revision_general' ? inputMode : 'amount'

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)

    const payload = {
      estimate_revision_id: revisionId,
      description,
      // See the matching comment in the Indirect Cost Item form above --
      // same UI key, same DB-column mismatch, same fix: only the value
      // actually persisted is translated to 'specific', which is what
      // boq_item_specific_risk / wbs_package_specific_risk actually filter on.
      allocation_scope: allocationScope === 'boq_specific' ? 'specific' : allocationScope,
      allocation_wbs_node_id: allocationScope === 'package_specific' ? allocationRef : null,
      allocation_boq_item_id: allocationScope === 'boq_specific' ? allocationRef : null,
      amount: effectiveMode === 'amount' ? Number(amount) : null,
      pct_of_direct_cost: effectiveMode === 'pct' ? Number(pct) : null,
      planned_monthly_distribution: monthlyDistribution,
      from_month: fromMonth || null,
      to_month: toMonth || null,
      package_id: packageId,
      department: department || null,
    }

    const { error } = editingId
      ? await supabase.from('risk_contingency_line').update(payload).eq('id', editingId)
      : await supabase.from('risk_contingency_line').insert(payload)
    if (error) setError(error.message)
    else {
      setSuccessMsg(editingId ? 'Risk/contingency line updated.' : 'Risk/contingency line added.')
      setDescription(''); setAmount(''); setPct(''); setAllocationRef('')
      setPackageId(null); setDepartment('')
      setFromMonth(''); setToMonth(''); setMonthlyDistribution({}); setRiskPattern('manual')
      setEditingId(null)
      await load()
      await shared.refreshSharedOptions(); await refreshTotals()
    }
    setSubmitting(false)
  }

  function startEdit(line) {
    setEditingId(line.id)
    setDescription(line.description)
    // See the matching comment in the Indirect Cost Item startEdit above.
    setAllocationScope(line.allocation_scope === 'specific' ? 'boq_specific' : line.allocation_scope)
    setAllocationRef(line.allocation_wbs_node_id || line.allocation_boq_item_id || '')
    setInputMode(line.pct_of_direct_cost != null ? 'pct' : 'amount')
    setPct(line.pct_of_direct_cost ?? '')
    setAmount(line.amount ?? '')
    setPackageId(line.package_id || null)
    setDepartment(line.department || '')
    setMonthlyDistribution(line.planned_monthly_distribution || {})
    // Now stored explicitly (from_month/to_month columns, same pattern
    // as Indirect), so editing a line restores its actual period
    // instead of forcing the Estimator to re-pick it.
    setFromMonth(line.from_month || '')
    setToMonth(line.to_month || '')
    setRiskPattern('manual')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function cancelEdit() {
    setEditingId(null)
    setDescription(''); setAmount(''); setPct(''); setAllocationRef('')
    setPackageId(null); setDepartment('')
    setFromMonth(''); setToMonth(''); setMonthlyDistribution({}); setRiskPattern('manual')
  }

  async function handleDelete(id) {
    setError(null)
    const { error } = await supabase.from('risk_contingency_line').delete().eq('id', id)
    if (error) setError(error.message)
    else { setSuccessMsg('Removed.'); await load() }
  }

  // Saves phasing only. This never touches amount / pct_of_direct_cost,
  // so it cannot change the calculated Risk value or Tender Summary --
  // it only records how an already-calculated amount is planned across
  // months.
  async function handleSaveDistribution(lineId, dist) {
    setError(null)
    const { error } = await supabase.from('risk_contingency_line').update({ planned_monthly_distribution: dist }).eq('id', lineId)
    if (error) setError(error.message)
    else { setSuccessMsg('Monthly distribution saved.'); setDistEditor(null); await load() }
  }

  function scopeLabel(line) {
    if (line.allocation_scope === 'revision_general') return 'Revision General'
    if (line.allocation_scope === 'package_specific') return `Package: ${packages.find(p => p.id === line.allocation_wbs_node_id)?.name || '—'}`
    return `BOQ: ${boqItems.find(b => b.id === line.allocation_boq_item_id)?.item_no || '—'}`
  }

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 4px' }}>Risk &amp; Contingency</h2>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 12px' }}>
        % of Direct Cost is only available for Revision-General risk. Package/BOQ-specific risk must use a fixed amount.
      </p>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {!isFrozen && (
      <>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} required style={{ width: 220 }} />
        </Field>
        <Field label="Allocation">
          <Select value={allocationScope} onChange={(e) => { setAllocationScope(e.target.value); setAllocationRef(''); setInputMode('pct') }} style={{ width: 160 }}>
            <option value="revision_general">Revision General</option>
            <option value="package_specific">Package-specific</option>
            <option value="boq_specific">BOQ-specific</option>
          </Select>
        </Field>
        {allocationScope === 'package_specific' && (
          <Field label="Package">
            <Select value={allocationRef} onChange={(e) => setAllocationRef(e.target.value)} required style={{ width: 200 }}>
              <option value="" disabled>Select…</option>
              {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}
        {allocationScope === 'boq_specific' && (
          <Field label="BOQ Item">
            <Select value={allocationRef} onChange={(e) => setAllocationRef(e.target.value)} required style={{ width: 240 }}>
              <option value="" disabled>Select…</option>
              {boqItems.map(b => <option key={b.id} value={b.id}>{b.item_no} — {b.description}</option>)}
            </Select>
          </Field>
        )}
        {allocationScope === 'revision_general' && (
          <Field label="Input As">
            <Select value={inputMode} onChange={(e) => setInputMode(e.target.value)} style={{ width: 130 }}>
              <option value="pct">% of Direct Cost</option>
              <option value="amount">Fixed Amount</option>
            </Select>
          </Field>
        )}
        {effectiveMode === 'pct' ? (
          <Field label="Percent">
            <Input type="number" step="any" min="0" value={pct} onChange={(e) => setPct(e.target.value)} required style={{ width: 100 }} />
          </Field>
        ) : (
          <Field label="Amount">
            <Input type="number" step="any" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required style={{ width: 120 }} />
          </Field>
        )}
        <Field label="Cost Package">
          <button type="button" onClick={() => setShowPackagePicker(true)}
            style={{ fontSize: 12, padding: '6px 10px', border: '1px solid #DDD9D0', borderRadius: 5, background: '#fff', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
            {shared.packageNameById?.[packageId] || '— Select —'}
          </button>
        </Field>
        <Field label="Department / Category">
          <ComboBox value={department} onChange={(v) => setDepartment(v || '')}
            options={shared.departments}
            placeholder="e.g. Risk & Planning Dept." />
        </Field>
        <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : (editingId ? 'Update' : '+ Add')}</Button>
        {editingId && <Button type="button" variant="secondary" onClick={cancelEdit}>Cancel</Button>}
      </form>

      {/* Monthly Distribution lives inside the same Add/Edit flow as
          every other field -- a single "Edit" action on the row below
          opens this whole form, matching the Indirect section exactly
          instead of requiring a second separate "Edit Distribution"
          button. */}
      <div style={{ padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Planned Monthly Distribution ({fromMonth || '—'} to {toMonth || '—'})</span>
          <Field label="From Month"><Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} style={{ width: 150 }} /></Field>
          <Field label="To Month"><Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} style={{ width: 150 }} /></Field>
          <Field label="Pattern">
            <Select value={riskPattern} onChange={(e) => {
              const p = e.target.value
              setRiskPattern(p)
              if (p === 'manual') return
              const keys = generateMonthKeys(fromMonth, toMonth)
              const budget = effectiveMode === 'amount' ? Number(amount || 0) : calcTotal
              if (keys.length > 0) setMonthlyDistribution(applyDistributionPattern(p, budget, keys))
            }} style={{ width: 140 }}>
              <option value="manual">Manual</option>
              <option value="even">Even</option>
              <option value="s_curve">S-Curve</option>
              <option value="front_loaded">Front-Loaded</option>
              <option value="back_loaded">Back-Loaded</option>
            </Select>
          </Field>
        </div>
        {(() => {
          const monthKeys = generateMonthKeys(fromMonth, toMonth)
          const keysToShow = monthKeys.length > 0 ? monthKeys : sortMonthKeys(Object.keys(monthlyDistribution))
          const distSum = Object.values(monthlyDistribution).reduce((s, v) => s + (Number(v) || 0), 0)
          return keysToShow.length > 0 ? (
            <>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginBottom: 10, paddingBottom: 8 }}>
                {keysToShow.map((m) => (
                  <div key={m} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 }}>
                    <label style={{ fontSize: 10, color: colors.textMuted, whiteSpace: 'nowrap' }}>{m}</label>
                    <Input type="number" step="any" value={monthlyDistribution[m] || ''} onChange={(e) => setMonthlyDistribution((prev) => ({ ...prev, [m]: Number(e.target.value) || 0 }))} style={{ width: 100 }} />
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12 }}>Planned Total: <strong>{formatNumber(distSum)}</strong></div>
            </>
          ) : (
            <div style={{ padding: '10px 14px', background: '#F1F5F9', borderRadius: 6, fontSize: 12, color: colors.textMuted }}>
              Select a From/To period above to plan the monthly distribution.
            </div>
          )
        })()}
      </div>
      </>
      )}

      <div style={{ padding: '10px 16px', background: '#F1F5F9', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
        Calculated Risk &amp; Contingency (Tender Summary): <strong>{formatNumber(calcTotal)}</strong> {shared.tenderCurrency || ''}
      </div>

      {/* Represented in the same shape as normal Indirect items --
          Qty=1, Unit=LS, Rate=Tender Budget -- for CostSpace Indirect
          Budget compatibility, while the original % / amount basis is
          preserved internally for audit (shown in the Basis column). */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'auto' }}>
        {lines === null && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={3} /></div>}
        {lines?.length === 0 && <div style={{ padding: 20 }}><EmptyState title="No risk/contingency lines yet" /></div>}
        {lines?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
              <Th></Th><Th>Description</Th><Th>Cost Package</Th><Th>Department / Category</Th><Th>Allocation</Th><Th align="right">Qty</Th><Th>Unit</Th><Th align="right">Rate</Th><Th align="right">Tender Budget</Th><Th align="right">Basis</Th><Th>Monthly Distribution</Th><Th></Th>
            </tr></thead>
            <tbody>
              {lines.map(l => {
                const dist = l.planned_monthly_distribution || {}
                const months = sortMonthKeys(Object.keys(dist))
                // For revision-general % lines, this line's own SAR
                // value equals calcTotal only when it is the sole such
                // line (the common case); a fixed-amount line's value
                // is its own `amount`, which the existing SSOT already
                // includes in calcTotal.
                // Each line's own SAR value: percentage-based lines are
                // pct_of_direct_cost/100 * direct_cost_total (the same
                // formula the authoritative SSOT view uses per-line
                // before summing); fixed-amount lines use their own
                // amount directly. Using calcTotal (the SUM of all
                // lines) for every individual line was the bug that
                // made two different-percentage lines show the same
                // Rate.
                const lineBudget = l.amount != null
                  ? Number(l.amount)
                  : (Number(l.pct_of_direct_cost || 0) / 100) * Number(directCostTotal || 0)
                // Presentation-only monthly representation, derived from
                // the actual existing distribution -- NOT a new
                // financial model. lineBudget (the authoritative total)
                // is unchanged; this is only Qty/Rate for display.
                const riskQty = months.length
                const riskRate = riskQty > 0 ? lineBudget / riskQty : null
                const isOpen = expandedLines.has(l.id)
                return (
                  <>
                    <tr key={l.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <Td>
                        {(months.length > 0) && (
                          <span style={{ cursor: 'pointer' }} onClick={() => setExpandedLines((prev) => {
                            const next = new Set(prev); next.has(l.id) ? next.delete(l.id) : next.add(l.id); return next
                          })}>{isOpen ? '▾' : '▸'}</span>
                        )}
                      </Td>
                      <Td>{l.description}</Td>
                      <Td muted>{l.package_name || '—'}</Td>
                      <Td muted>{l.department || '—'}</Td>
                      <Td muted>{scopeLabel(l)}</Td>
                      <Td align="right">{riskQty > 0 ? formatNumber(riskQty) : '—'}</Td>
                      <Td muted>{riskQty > 0 ? 'month' : '—'}</Td>
                      <Td align="right">{riskRate != null ? formatNumber(riskRate) : '—'}</Td>
                      <Td align="right">{formatNumber(lineBudget)}</Td>
                      <Td align="right" muted>{l.pct_of_direct_cost != null ? formatPercent(l.pct_of_direct_cost) : formatNumber(l.amount)}</Td>
                      <Td>
                        {months.length === 0 ? (
                          <span style={{ color: '#92400E', fontWeight: 600, fontSize: 12 }}>⚠ No distribution</span>
                        ) : (() => {
                          const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
                          const lineReconciled = Math.abs(lineBudget - distSum) <= 0.01
                          return lineReconciled
                            ? <span style={{ color: '#065F46', fontWeight: 600, fontSize: 12 }}>✅ {months.length} months</span>
                            : <span style={{ color: '#991B1B', fontWeight: 600, fontSize: 12 }}>🚫 Mismatch ({formatNumber(distSum)} vs {formatNumber(lineBudget)})</span>
                        })()}
                      </Td>
                      <Td align="right">
                        <button onClick={() => startEdit(l)} style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: 13, marginRight: 10 }}>Edit</button>
                        {!isFrozen && <button onClick={() => handleDelete(l.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 13 }}>Remove</button>}
                      </Td>
                    </tr>
                    {isOpen && months.length > 0 && (
                      <tr><td colSpan={12} style={{ padding: '0 0 14px 0' }}>
                        <DistributionTable dist={dist} />
                      </td></tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Display-only total, read verbatim from
          estimate_revision_general_contingency via
          estimate_revision_calculation_chain -- never summed in
          React. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 4px', borderTop: `2px solid ${colors.border}`, marginTop: -1 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
          Risk &amp; Contingency Total: {formatNumber(sectionTotal ?? 0)} {shared.tenderCurrency || ''}
        </span>
      </div>
      {showPackagePicker && (
        <PackagePickerModal
          tenderId={tenderId}
          currentPackageId={packageId}
          onSelect={async (id) => { setPackageId(id); setShowPackagePicker(false); await shared.refreshSharedOptions() }}
          onClose={() => setShowPackagePicker(false)}
        />
      )}
    </section>
  )
}

// Shared phasing editor for Risk & HO lines -- a Qty=1 / Unit=LS
// monetary provision phased across a From/To month range. Reuses
// generateMonthKeys so a "project month" means the same thing
// everywhere. lineBudget may be null (percentage-based lines don't
// expose a per-line SAR split from the existing SSOT); in that case
// reconciliation is not enforced and the Estimator phases freely.
function MonthlyDistEditor({ lineBudget, initial, onCancel, onSave }) {
  const [fromMonth, setFromMonth] = useState('')
  const [toMonth, setToMonth] = useState('')
  const [pattern, setPattern] = useState('manual')
  const [values, setValues] = useState(initial || {})
  const monthKeys = generateMonthKeys(fromMonth, toMonth)
  const keysToShow = monthKeys.length > 0 ? monthKeys : Object.keys(values)
  const sum = Object.values(values).reduce((s, v) => s + (Number(v) || 0), 0)
  const variance = lineBudget != null ? lineBudget - sum : null
  const reconciled = variance == null || Math.abs(variance) <= 0.01

  return (
    <div style={{ padding: 12, background: '#FAFBFC', border: `1px solid ${colors.border}`, borderRadius: 6 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <Field label="From Month"><Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} style={{ width: 150 }} /></Field>
        <Field label="To Month"><Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} style={{ width: 150 }} /></Field>
        <Field label="Pattern">
          <Select value={pattern} onChange={(e) => {
            const p = e.target.value
            setPattern(p)
            if (p !== 'manual' && lineBudget != null && monthKeys.length > 0) {
              setValues(applyDistributionPattern(p, lineBudget, monthKeys))
            }
          }} style={{ width: 140 }} disabled={monthKeys.length === 0 || lineBudget == null}>
            <option value="manual">Manual</option>
            <option value="even">Even</option>
            <option value="s_curve">S-Curve</option>
            <option value="front_loaded">Front-Loaded</option>
            <option value="back_loaded">Back-Loaded</option>
          </Select>
        </Field>
      </div>
      {keysToShow.length > 0 && (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginBottom: 10, paddingBottom: 8 }}>
          {keysToShow.map((m) => (
            <div key={m} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 }}>
              <label style={{ fontSize: 10, color: colors.textMuted, whiteSpace: 'nowrap' }}>{m}</label>
              <Input type="number" step="any" value={values[m] || ''} onChange={(e) => setValues((prev) => ({ ...prev, [m]: Number(e.target.value) || 0 }))} style={{ width: 100 }} />
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 20, fontSize: 12, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>Planned Total: <strong>{formatNumber(sum)}</strong></span>
        {lineBudget != null && (
          <>
            <span>Budget: <strong>{formatNumber(lineBudget)}</strong></span>
            <span style={{ color: reconciled ? '#065F46' : '#991B1B', fontWeight: 700 }}>{reconciled ? '✅ Reconciled' : '🚫 Not Reconciled'}</span>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={() => onSave(values)} disabled={lineBudget != null && !reconciled}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}

function HoOverheadSection({ revisionId, tenderId, shared, sectionTotal, isFrozen, refreshTotals }) {
  const [setting, setSetting] = useState(undefined)
  const [pctInput, setPctInput] = useState('')
  const [packageId, setPackageId] = useState(null)
  const [showPackagePicker, setShowPackagePicker] = useState(false)
  const [department, setDepartment] = useState('')
  const [fromMonth, setFromMonth] = useState('')
  const [toMonth, setToMonth] = useState('')
  const [monthlyDistribution, setMonthlyDistribution] = useState({})
  const [hoPattern, setHoPattern] = useState('manual')
  const [calcTotal, setCalcTotal] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const load = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    const frozen = revRes.data?.status === 'frozen'

    if (frozen) {
      // Frozen: read ho_overhead_setting and the authoritative total
      // from the immutable freeze_snapshot instead of the live (empty)
      // table/View.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('ho_overhead_total, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      const indirect = snapRes.data?.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      const frozenSetting = indirect.ho_overhead_setting && typeof indirect.ho_overhead_setting === 'object' ? indirect.ho_overhead_setting : null
      setSetting(frozenSetting)
      setPctInput(frozenSetting?.ho_overhead_pct ?? '')
      setPackageId(frozenSetting?.package_id || null)
      setDepartment(frozenSetting?.department || '')
      setFromMonth(frozenSetting?.from_month || '')
      setToMonth(frozenSetting?.to_month || '')
      setMonthlyDistribution(frozenSetting?.planned_monthly_distribution || {})
      setHoPattern('manual')
      // Never auto-open the Add/Edit form for a frozen revision, even
      // when no setting was captured -- editing is disabled here anyway.
      setIsEditing(false)
      setCalcTotal(snapRes.data?.ho_overhead_total ?? 0)
      return
    }

    const [settingRes, chainRes] = await Promise.all([
      supabase.from('ho_overhead_setting').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
      supabase.from('estimate_revision_calculation_chain').select('ho_overhead_total').eq('estimate_revision_id', revisionId).maybeSingle(),
    ])
    if (settingRes.error) setError(settingRes.error.message)
    else {
      setSetting(settingRes.data)
      setPctInput(settingRes.data?.ho_overhead_pct ?? '')
      setPackageId(settingRes.data?.package_id || null)
      setDepartment(settingRes.data?.department || '')
      // Same explicit from_month/to_month pattern as Indirect and Risk,
      // so editing restores the actual saved period instead of losing it.
      setFromMonth(settingRes.data?.from_month || '')
      setToMonth(settingRes.data?.to_month || '')
      setMonthlyDistribution(settingRes.data?.planned_monthly_distribution || {})
      setHoPattern('manual')
      // No HO setting yet for this revision -- open the form directly
      // so the Estimator can enter it, matching Indirect/Risk "Add" mode.
      setIsEditing(!settingRes.data)
    }
    setCalcTotal(chainRes.data?.ho_overhead_total ?? 0)
  }, [revisionId])

  useEffect(() => { load() }, [load])

  async function handleSave(e) {
    e.preventDefault()
    setError(null)
    setSuccessMsg(null)

    // package_name/department/monthly phasing are all classification/
    // phasing fields, saved alongside the single HO row -- never
    // affecting ho_overhead_pct or the calculated total.
    const payload = {
      ho_overhead_pct: Number(pctInput),
      package_id: packageId,
      department: department || null,
      from_month: fromMonth || null,
      to_month: toMonth || null,
      planned_monthly_distribution: monthlyDistribution,
    }

    if (setting) {
      const { error } = await supabase.from('ho_overhead_setting').update(payload).eq('id', setting.id)
      if (error) setError(error.message)
      else { setSuccessMsg('HO Overhead updated.'); setIsEditing(false); await load(); await shared.refreshSharedOptions(); await refreshTotals() }
    } else {
      const { error } = await supabase.from('ho_overhead_setting').insert({
        estimate_revision_id: revisionId, base_rule: 'direct_plus_site_overhead', ...payload,
      })
      if (error) setError(error.message)
      else { setSuccessMsg('HO Overhead set.'); setIsEditing(false); await load(); await shared.refreshSharedOptions(); await refreshTotals() }
    }
  }

  const dist = setting?.planned_monthly_distribution || {}
  const distMonths = sortMonthKeys(Object.keys(dist))

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 4px' }}>Head Office (HO) Overhead</h2>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 12px' }}>
        Applied as % of (Direct Cost + General Site Overhead). No markup applies to HO Overhead itself.
      </p>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {setting === undefined && <SkeletonRows rows={1} cols={1} />}

      {setting !== undefined && (
        <>
          {isEditing ? (
            <>
              <form onSubmit={handleSave} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <Field label="HO Overhead %">
                  <Input type="number" step="any" min="0" value={pctInput} onChange={(e) => setPctInput(e.target.value)} required style={{ width: 120 }} />
                </Field>
                <Field label="Cost Package">
                  <button type="button" onClick={() => setShowPackagePicker(true)}
                    style={{ fontSize: 12, padding: '6px 10px', border: '1px solid #DDD9D0', borderRadius: 5, background: '#fff', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                    {shared.packageNameById?.[packageId] || '— Select —'}
                  </button>
                </Field>
                <Field label="Department / Category">
                  <ComboBox value={department} onChange={(v) => setDepartment(v || '')} options={shared.departments} placeholder="e.g. Finance Dept." />
                </Field>
                <Field label="From Month"><Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} style={{ width: 150 }} /></Field>
                <Field label="To Month"><Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} style={{ width: 150 }} /></Field>
                <Field label="Pattern">
                  <Select value={hoPattern} onChange={(e) => {
                    const p = e.target.value
                    setHoPattern(p)
                    if (p === 'manual') return
                    const keys = generateMonthKeys(fromMonth, toMonth)
                    if (keys.length > 0) setMonthlyDistribution(applyDistributionPattern(p, Number(calcTotal || 0), keys))
                  }} style={{ width: 140 }}>
                    <option value="manual">Manual</option>
                    <option value="even">Even</option>
                    <option value="s_curve">S-Curve</option>
                    <option value="front_loaded">Front-Loaded</option>
                    <option value="back_loaded">Back-Loaded</option>
                  </Select>
                </Field>
                <Button type="submit">{setting ? 'Update' : 'Set'}</Button>
                <Button type="button" variant="secondary" onClick={() => {
                  setIsEditing(false)
                  // Revert any unsaved in-form edits back to the last loaded state.
                  setPctInput(setting?.ho_overhead_pct ?? '')
                  setPackageId(setting?.package_id || null)
                  setDepartment(setting?.department || '')
                  setFromMonth(setting?.from_month || '')
                  setToMonth(setting?.to_month || '')
                  setMonthlyDistribution(setting?.planned_monthly_distribution || {})
                }}>Cancel</Button>
              </form>

              {(() => {
                const monthKeys = generateMonthKeys(fromMonth, toMonth)
                const keysToShow = monthKeys.length > 0 ? monthKeys : sortMonthKeys(Object.keys(monthlyDistribution))
                const distSum = Object.values(monthlyDistribution).reduce((s, v) => s + (Number(v) || 0), 0)
                return keysToShow.length > 0 ? (
                  <div style={{ padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginBottom: 10, paddingBottom: 8 }}>
                      {keysToShow.map((m) => (
                        <div key={m} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 }}>
                          <label style={{ fontSize: 10, color: colors.textMuted, whiteSpace: 'nowrap' }}>{m}</label>
                          <Input type="number" step="any" value={monthlyDistribution[m] || ''} onChange={(e) => setMonthlyDistribution((prev) => ({ ...prev, [m]: Number(e.target.value) || 0 }))} style={{ width: 100 }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12 }}>Planned Total: <strong>{formatNumber(distSum)}</strong></div>
                  </div>
                ) : null
              })()}
            </>
          ) : (
            // Collapsed summary row -- matches the read-only row shape
            // used by Indirect and Risk lines, with a single Edit
            // action that opens the form above.
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13 }}>HO % : <strong>{formatPercent(setting?.ho_overhead_pct ?? 0)}</strong></span>
                <span style={{ fontSize: 13 }}>Package: <strong>{setting?.package_name || '—'}</strong></span>
                <span style={{ fontSize: 13 }}>Department: <strong>{setting?.department || '—'}</strong></span>
                {/* Presentation-only Qty/Rate, derived from the actual
                    existing distribution -- ho_overhead_total (Tender
                    Budget) remains the sole financial SSOT, unchanged. */}
                <span style={{ fontSize: 13 }}>Qty: <strong>{distMonths.length > 0 ? formatNumber(distMonths.length) : '—'}</strong></span>
                <span style={{ fontSize: 13 }}>Unit: <strong>{distMonths.length > 0 ? 'month' : '—'}</strong></span>
                <span style={{ fontSize: 13 }}>Rate: <strong>{distMonths.length > 0 ? formatNumber(Number(calcTotal || 0) / distMonths.length) : '—'}</strong></span>
                <span style={{ fontSize: 13 }}>Tender Budget: <strong>{formatNumber(calcTotal)}</strong></span>
                {distMonths.length > 0 ? (() => {
                  const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
                  const reconciled = Math.abs(Number(calcTotal || 0) - distSum) <= 0.01
                  return (
                    <span onClick={() => setExpanded((v) => !v)} style={{ cursor: 'pointer', color: reconciled ? '#065F46' : '#991B1B', fontWeight: 600, fontSize: 12 }}>
                      {expanded ? '▾' : '▸'} {reconciled ? `✅ ${distMonths.length} months` : `🚫 Mismatch (${formatNumber(distSum)} vs ${formatNumber(calcTotal)})`}
                    </span>
                  )
                })() : (
                  <span style={{ color: '#92400E', fontWeight: 600, fontSize: 12 }}>⚠ No distribution</span>
                )}
                {!isFrozen && <button onClick={() => setIsEditing(true)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: 13 }}>Edit</button>}
              </div>
              {expanded && distMonths.length > 0 && (
                <DistributionTable dist={dist} />
              )}
            </div>
          )}
        </>
      )}
      {/* Display-only total, read verbatim from
          estimate_revision_calculation_chain.ho_overhead_total --
          never recalculated in React. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 4px', borderTop: `2px solid ${colors.border}`, marginTop: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
          HO Overhead Total: {formatNumber(sectionTotal ?? 0)} {shared.tenderCurrency || ''}
        </span>
      </div>
      {showPackagePicker && (
        <PackagePickerModal
          tenderId={tenderId}
          currentPackageId={packageId}
          onSelect={async (id) => { setPackageId(id); setShowPackagePicker(false); await shared.refreshSharedOptions() }}
          onClose={() => setShowPackagePicker(false)}
        />
      )}
    </section>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {label}{children}
    </label>
  )
}
function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted }) {
  return <td style={{ textAlign: align, padding: '11px 16px', fontSize: 14, color: muted ? colors.textMuted : colors.textPrimary }}>{children}</td>
}
